import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import {
  DatePicker,
  FormLabel,
  ModalError,
  ModalSuccess,
  MultipleSelectInputWithTags,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "components";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { hardBaseUrl } from "services/urlConstant";
import { formatPayloadDate, getErrors } from "utils";

const ModalFormParameterGroup = ({ open, onClose, preload, getDataTable }) => {
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [payload, setPayload] = useState({
    group_name: "",
    composer_id: [],
    start_date: moment(),
    end_date: moment(),
  });
  const [optionComposer, setOptionComposer] = useState([]);
  const [selectedComposer, setSelectedComposer] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [hasMore, setHasMore] = useState(true);
  const [minEndPeriod, setMinEndPeriod] = useState(moment());

  const handleChangePayload = (value, key) => {
    setPayload(currentState => ({
      ...currentState,
      [key]: value,
    }));
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };
  const handleChangeMember = (event, option) => {
    const { checked } = event?.target || false;
    setSelectedComposer(prev =>
      checked
        ? [...prev, option]
        : prev.filter(item => item.composer_id !== option.composer_id)
    );
  };
  const onChangeStartDate = date => {
    const startPeriod = moment(date);
    const endPeriod = moment(payload?.end_date);
    if (startPeriod.isAfter(endPeriod)) {
      handleChangePayload(startPeriod, "end_date");
    }
    handleChangePayload(startPeriod, "start_date");
  };
  const onChangeEndDate = date => {
    const endPeriod = moment(date);
    const startPeriod = moment(payload?.start_date);
    if (endPeriod.isBefore(startPeriod)) {
      handleChangePayload(endPeriod, "start_date");
    }
    handleChangePayload(endPeriod, "end_date");
  };

  const getOptionComposer = async () => {
    try {
      setLoadingFilter(true);
      const res = await axios.get(`${hardBaseUrl}/publisher/composer`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      const newOptions = (data || [])?.map(item => ({
        ...item,
        id: item?.composer_id,
      }));
      setOptionComposer(prev =>
        queryParams.page === 1 ? newOptions : [...prev, ...newOptions]
      );
      setHasMore(meta?.total > newOptions.length);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingFilter(false);
    }
  };
  const getDetail = async () => {
    setLoadingPage(true);
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/group/detail/${preload}`,
        { headers }
      );
      const { group_name, composers, start_date, end_date } = res?.data?.data;
      setPayload({
        group_name,
        composer_id: composers?.map(composer => composer?.composer_id),
        start_date: moment(start_date),
        end_date: moment(end_date),
      });
      setSelectedComposer(composers);
      setMinEndPeriod(moment(end_date));
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingPage(false);
    }
  };
  const handleSubmit = async () => {
    const method = preload ? axios.put : axios.post;
    const { group_name, start_date, end_date } = payload;
    const modifiedPayload = {
      group_name,
      composer_id: selectedComposer?.map(composer => composer?.composer_id),
      start_date: formatPayloadDate(start_date),
      end_date: formatPayloadDate(end_date),
    };
    try {
      setLoadingButton(true);
      await method(
        `${hardBaseUrl}/publisher/group${
          preload ? `/update/${preload}` : "/create"
        }`,
        modifiedPayload,
        { headers }
      );
      ModalSuccess(
        `Successfully ${preload ? "update" : "save"} group`
      ).then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
      onClose();
    }
  };

  useEffect(() => {
    if (preload) {
      getDetail();
    }
  }, [preload]);
  useEffect(() => {
    getOptionComposer();
  }, [queryParams]);
  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>{`${preload ? "Edit" : "Add"} Group`}</DialogTitle>
      <DialogContent dividers>
        {loadingPage ? (
          <Grid container direction="column" rowSpacing={2}>
            {Object.keys(payload).map((_, index) => (
              <Grid item key={index}>
                <Box mb="8px">
                  <Skeleton variant="rounded" width="40%" height={25} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={30} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container direction="column" rowSpacing={1}>
            {MockFieldForm?.map(({ title, key, type }, index) => (
              <Grid item key={index}>
                <FormLabel label={title} required />
                {type === "select" ? (
                  <MultipleSelectInputWithTags
                    placeholder={"Select Member"}
                    value={selectedComposer}
                    options={optionComposer}
                    optionKey="composer_id"
                    optionLabel="sure_name"
                    onChange={handleChangeMember}
                    search
                    searchValue={queryParams?.search}
                    onChangeSearch={value =>
                      handleChangeQueryParams(value, "search")
                    }
                    setQueryParams={setQueryParams}
                    hasMore={hasMore}
                    loading={loadingFilter}
                    customPaperWidth={552}
                  />
                ) : type === "date" ? (
                  <DatePicker
                    placeholder={title}
                    value={payload?.[key]}
                    minDate={
                      key === "end_date" ? minEndPeriod : moment("2010-01-01")
                    }
                    maxDate={
                      key === "end_date" ? moment().add(10, "years") : moment()
                    }
                    onChange={
                      key === "end_date" ? onChangeEndDate : onChangeStartDate
                    }
                    width="100%"
                  />
                ) : (
                  <TextInput
                    placeholder={title}
                    value={payload?.[key]}
                    onChange={e => handleChangePayload(e?.target?.value, key)}
                    disabled={loadingButton}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton
          label="Cancel"
          disabled={loadingPage}
          onClick={onClose}
        />
        <PrimaryButton
          label={loadingButton ? "Saving" : "Save"}
          loading={loadingButton}
          disabled={loadingPage || loadingButton}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

const MockFieldForm = [
  {
    title: "Name",
    key: "group_name",
  },
  {
    title: "Member",
    type: "select",
  },
  {
    title: "Start Period",
    key: "start_date",
    type: "date",
  },
  {
    title: "End Period",
    key: "end_date",
    type: "date",
  },
];
export default ModalFormParameterGroup;
